import {
  TextInput,
  PasswordInput,
  Paper,
  Container,
  Title,
  Text,
  Button,
  Group,
  Anchor,
  Center,
  Box,
  Alert,
} from "@mantine/core";
import { IconArrowLeft, IconAlertCircle } from "@tabler/icons";
import { message } from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageTitle } from "../../components";
import { postAcceptFacilityInvitation } from "../../helpers/api";
import { appLinks } from "../../helpers/config";
import { useForm } from "react-hook-form";

const useStyles = {
  title: {
    fontSize: 26,
    fontWeight: 900,
  },
  controls: {
    "@media (max-width: 576px)": {
      flexDirection: "column-reverse",
    },
  },
  control: {
    "@media (max-width: 576px)": {
      width: "100%",
      textAlign: "center",
    },
  },
};

export function AcceptFacilityInvitationPage() {
  const [busy, setBusy] = useState(false);
  const [isNewUser, setIsNewUser] = useState(true);
  const [invitationEmail, setInvitationEmail] = useState("");
  const [facilityName, setFacilityName] = useState("");
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    const key = searchParams.get("key");
    const email = searchParams.get("email");
    const facility = searchParams.get("facility");
    const newUser = searchParams.get("newUser");

    if (!key || !email) {
      message.error("Invalid invitation link");
      setTimeout(() => nav(appLinks.login), 2000);
      return;
    }

    setInvitationEmail(email);
    if (facility) {
      setFacilityName(decodeURIComponent(facility));
    }
    setIsNewUser(newUser === "true");
  }, [searchParams, nav]);

  const onSubmit = async (data) => {
    const key = searchParams.get("key");
    const email = searchParams.get("email");

    if (!key || !email) {
      message.error("Invalid invitation link");
      return;
    }

    // For new users, all fields are required
    if (isNewUser) {
      if (!data.name || !data.contact || !data.password) {
        message.error("All fields are required for new users");
        return;
      }
    }

    setBusy(true);

    try {
      const response = await postAcceptFacilityInvitation({
        key,
        email,
        name: data.name || undefined,
        contact: data.contact || undefined,
        password: data.password || undefined,
      });

      if (response.status === 200 || response.status === 201) {
        message.success(
          response.data?.message || "Invitation accepted successfully"
        );
        setTimeout(() => {
          nav(appLinks.login);
        }, 1500);
      } else {
        throw response;
      }
    } catch (ex) {
      const errorMsg = ex?.response?.data?.message;
      message.error(
        errorMsg || "Failed to accept invitation. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  if (!invitationEmail) {
    return (
      <Container size={460} my={30}>
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Loading..."
          color="blue">
          Validating invitation...
        </Alert>
      </Container>
    );
  }

  return (
    <Container size={460} my={30}>
      <PageTitle title="Accept Facility Invitation" />
      <Title style={useStyles.title} align="center">
        Accept Facility Invitation
      </Title>
      <Text color="dimmed" size="sm" align="center" mt="md">
        {facilityName
          ? `Complete your profile to join '${facilityName}'`
          : "Complete your profile to join the facility"}
      </Text>

      <form onSubmit={handleSubmit(onSubmit)} method="POST">
        <Paper withBorder shadow="md" p={30} radius="md" mt="xl">
          <TextInput
            label="Email"
            value={invitationEmail}
            disabled
            size="md"
            mb="md"
          />

          {isNewUser ? (
            <>
              <TextInput
                {...register("name", {
                  required: "Full name is required",
                })}
                label="Full Name"
                placeholder="Enter your full name"
                required
                size="md"
                mb="md"
                error={errors.name?.message}
              />

              <TextInput
                {...register("contact", {
                  required: "Contact number is required",
                  minLength: {
                    value: 10,
                    message: "Contact must be at least 10 digits",
                  },
                })}
                label="Phone Number"
                placeholder="e.g. 0200000000"
                required
                size="md"
                mb="md"
                error={errors.contact?.message}
              />

              <PasswordInput
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                })}
                label="Password"
                placeholder="Enter your password"
                required
                size="md"
                mb="md"
                error={errors.password?.message}
              />
            </>
          ) : (
            <Alert icon={<IconAlertCircle size={16} />} color="blue" mb="md">
              You already have an account. Click "Accept Invitation" to join
              <strong>{` ${facilityName}`}</strong>
            </Alert>
          )}

          <Group position="apart" mt="lg" style={useStyles.controls}>
            <Anchor
              color="dimmed"
              size="sm"
              style={useStyles.control}
              onClick={() => nav(appLinks.login)}>
              <Center inline>
                <IconArrowLeft size={12} stroke={1.5} />
                <Box ml={5}>Back to Login</Box>
              </Center>
            </Anchor>
            <Button
              type="submit"
              className="app-btn btn-prim"
              style={useStyles.control}
              loading={busy}>
              Accept Invitation
            </Button>
          </Group>
        </Paper>
      </form>
    </Container>
  );
}
